/**
 * RequireAuth - guard для защищённых маршрутов (/*, /admin/*)
 *
 * Если юзер не авторизован - редиректит на «/» и через query-параметр
 * `?auth=1` (для /*) или `?auth=admin` (для /admin/*) даёт лендингу
 * сигнал открыть AuthDialog сразу. Также сохраняем исходный путь в
 * `?next=...` чтобы вернуть пользователя обратно после входа
 *
 * isLoading - короткое окно (~200 мс) между mount и завершением
 * /api/auth/me. В это время держим spinner, иначе будем флешить
 * редирект на «/» даже у валидно залогиненного юзера
 */
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/stores/auth";

interface RequireAuthProps {
  /** Требовать admin-роль. По умолчанию хватает любого user. */
  admin?: boolean;
}

export function RequireAuth({ admin = false }: RequireAuthProps) {
  const user = useAuth((s) => s.user);
  const authChecked = useAuth((s) => s.authChecked);
  const location = useLocation();

  if (!authChecked) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-ink-soft">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/?auth=1&next=${next}`} replace />;
  }

  if (admin && user.role !== "admin") {
    // Юзер есть, но не админ - на главную без auth-prompt.
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
